import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {Company} from '../../../../models/company';
import {
    AddressLookupResult,
    cityFromLookup,
    provinceCodeFromLookup
} from '../../../../services/address-lookup/address-lookup.service';

@Component({
    selector: 'app-companies-settings-company-data',
    templateUrl: './companies-settings-company-data.component.html',
    styles: [':host { display: contents !important; }']
})
export class CompaniesSettingsCompanyDataComponent implements OnDestroy {
    @Input() company: Company = new Company();
    @Input() provinces: { id: string; name: string }[] = [];
    @Input() cities: string[] = [];

    @Output() provinceChange = new EventEmitter<void>();

    private cityApplyTimeout: ReturnType<typeof setTimeout> | null = null;

    get fiscalCodeLength(): number {
        return (this.company.fiscalCode || '').replace(/\s+/g, '').length;
    }

    get vatLength(): number {
        return (this.company.vat || '').replace(/\D/g, '').length;
    }

    get ibanLength(): number {
        return (this.company.iban || '').replace(/\s+/g, '').length;
    }

    get formattedIban(): string {
        const iban = (this.company.iban || '').replace(/\s+/g, '').toUpperCase();
        return iban.replace(/(.{4})/g, '$1 ').trim();
    }

    normalizeFiscalCode(): void {
        if (typeof this.company.fiscalCode === 'string') {
            this.company.fiscalCode = this.company.fiscalCode.replace(/\s+/g, '').toUpperCase();
        }
    }

    normalizeVat(): void {
        if (typeof this.company.vat === 'string') {
            this.company.vat = this.company.vat.replace(/\D/g, '').slice(0, 11);
        }
    }

    normalizeSdi(): void {
        if (typeof this.company.sdi === 'string') {
            this.company.sdi = this.company.sdi.replace(/\s+/g, '').toUpperCase().slice(0, 7);
        }
    }

    onIbanInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.company.iban = (input.value ?? '').replace(/\s+/g, '').toUpperCase().slice(0, 27);
        input.value = this.formattedIban;
    }

    selectAddress(result: AddressLookupResult): void {
        const address = result.address ?? {};
        if (address.house_number) {
            this.company.addressNumber = address.house_number as unknown as number;
        }
        if (address.postcode) {
            this.company.zip = address.postcode;
        }

        const provinceCode = provinceCodeFromLookup(result, this.provinces);
        if (provinceCode) {
            this.company.province = provinceCode;
            this.provinceChange.emit();
        }
        this.scheduleCityApply(cityFromLookup(result));
    }

    onProvinceChange(): void {
        this.provinceChange.emit();
    }

    ngOnDestroy(): void {
        if (this.cityApplyTimeout) {
            clearTimeout(this.cityApplyTimeout);
        }
    }

    private scheduleCityApply(city?: string): void {
        if (!city) {
            return;
        }
        if (this.cityApplyTimeout) {
            clearTimeout(this.cityApplyTimeout);
        }
        this.cityApplyTimeout = setTimeout(() => {
            const normalized = this.normalizeName(city);
            this.company.city = this.cities.find((option) => this.normalizeName(option) === normalized) ?? city;
            this.cityApplyTimeout = null;
        });
    }

    private normalizeName(value: string): string {
        return value
            .toLocaleLowerCase('it')
            .replace(/provincia (di|del|della) /g, '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
    }
}
